// Explicit, bounded machine metadata. Never capture study inputs, DOM text or URLs.
const outcomes = ['started', 'attached', 'slow', 'ready', 'partial', 'recovered', 'stalled', 'timeout', 'error', 'abandoned', 'cancelled'];
const stages = ['initializing', 'series', 'scale', 'styles', 'studies', 'layout', 'complete'];
const number = (value, max) => Math.max(0, Math.min(max, Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0));
export function cleanChartDiagnostic(value = {}) {
  const result = { version: 1, stage: stages.includes(value.stage) ? value.stage : 'initializing' };
  if (/^[0-9]{10,16}-[0-9]{1,8}$/.test(value.load_id)) result.load_id = value.load_id;
  ['series_loaded', 'series_loading', 'ticker_matches', 'interval_matches', 'scale_ready', 'styles_ready', 'hidden', 'bridge_attached', 'cache_hit'].forEach(key => {
    if (typeof value[key] === 'boolean') result[key] = value[key];
  });
  ['elapsed_ms', 'bars_count', 'paints'].forEach(key => { if (value[key] != null) result[key] = number(value[key], key === 'elapsed_ms' ? 300000 : 1000000); });
  if (value.cache && typeof value.cache === 'object') {
    result.cache = {}; ['hits', 'misses', 'shared', 'pending'].forEach(key => { if (value.cache[key] != null) result.cache[key] = number(value.cache[key], 1000000); });
  }
  if (value.phase_ms && typeof value.phase_ms === 'object') {
    result.phase_ms = {}; ['series', 'scale', 'styles', 'layout'].forEach(key => { if (value.phase_ms[key] != null) result.phase_ms[key] = number(value.phase_ms[key], 300000); });
  }
  if (Array.isArray(value.frame_size)) result.frame_size = value.frame_size.slice(0, 2).map(v => number(v, 20000));
  if (Array.isArray(value.pending_studies)) result.pending_studies = value.pending_studies.slice(0, 12).map(study => ({
    study_id: typeof study.study_id === 'string' && /^[a-zA-Z0-9 ._@:-]{1,80}$/.test(study.study_id) ? study.study_id : 'unknown',
    loading_ms: number(study.loading_ms, 300000)
  }));
  if (Array.isArray(value.pending_styles)) result.pending_styles = value.pending_styles.slice(0, 12).filter(v => typeof v === 'string' && /^[a-zA-Z0-9_.-]{1,80}$/.test(v));
  if (['adapter', 'render', 'series-or-layout', 'context', 'selection', 'invalid-symbol', 'restricted-symbol'].includes(value.failure)) result.failure = value.failure;
  // Browser family/version only; the existing workspace snapshot records the viewport.
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const match = ua.match(/(Edg)\/([0-9]+)/) || ua.match(/(Firefox|Chrome)\/([0-9]+)/) || ua.match(/Version\/([0-9]+).*Safari/);
  if (match) result.browser = match.length === 3 ? `${match[1].toLowerCase()}:${match[2]}` : `safari:${match[1]}`;
  return result;
}
export function validChartOutcome(value) { return outcomes.includes(value); }
export function chartTimeoutSnapshot(frame, loadId, started) {
  const value = { load_id: loadId, elapsed_ms: Date.now() - started, stage: 'initializing' };
  try {
    const nested = frame.contentDocument.querySelector('#chart_container iframe');
    const series = nested.contentWindow.Q3.model().mainSeries();
    value.series_loaded = Boolean(series._seriesLoaded);
    value.series_loading = series._loadingStartTime != null;
    value.bars_count = series.bars().size();
    value.stage = value.series_loaded ? 'layout' : 'series';
    value.pending_studies = nested.contentWindow.Q3.model().model().Q19()
      .filter(source => source !== series && source._loadingStartTime != null && !source._failed)
      .slice(0, 12).map(source => ({ study_id: source._metaInfo && source._metaInfo.id || 'unknown', loading_ms: Date.now() - source._loadingStartTime }));
    value.pending_styles = [...nested.contentDocument.querySelectorAll('link[rel="stylesheet"]')]
      .filter(link => !link.sheet && !link.disabled && link.media !== 'not all')
      .map(link => new URL(link.href).pathname.split('/').pop());
    const rect = nested.getBoundingClientRect(); value.frame_size = [rect.width, rect.height];
    if (value.pending_studies.length) value.stage = 'studies';
    else if (value.pending_styles.length) value.stage = 'styles';
  } catch (_) { /* Cross-origin/uninitialized/removed frames remain a useful initialization diagnosis. */ }
  return cleanChartDiagnostic(value);
}
